import api from './api';
import { ReactionSummary } from '../utils/chatReactions';

// ===================================================================
// LiveChatService — generischer Gruppen-Live-Chat pro Entität
// (2026-08-27, zuerst für Tippspiele). Backend:
// dartcloud-backend/src/core/routes/live-chat-routes.js
// Realtime: Socket.io-Channel `live-chat:<ENTITY_TYPE>:<entityId>`,
// Events `liveChatMessage` / `liveChatMessageDeleted`.
// ===================================================================

/** URL-Segmente, die das Backend kennt (neuer Bereich = eine Zeile) */
export type LiveChatEntitySegment =
  | 'prediction-game'
  | 'tournament'
  | 'club'
  | 'pub'
  | 'shop'
  | 'series'
  | 'league'
  | 'association'
  | 'lobby';

const SEGMENT_TO_ENUM: Record<LiveChatEntitySegment, string> = {
  'prediction-game': 'PREDICTION_GAME',
  tournament: 'TOURNAMENT',
  club: 'CLUB',
  pub: 'PUB',
  shop: 'SHOP',
  series: 'SERIES',
  league: 'LEAGUE',
  association: 'ASSOCIATION',
  // Globaler Community-Chat der Startseite: entityId ist immer 'global'
  lobby: 'LOBBY',
};

/** Feste entityId des globalen Lobby-Chats (Kanal `live-chat:LOBBY:global`) */
export const LOBBY_GLOBAL_ID = 'global';

export interface LiveChatMessage {
  id?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  senderId?: string | null;
  senderName?: string | null;
  senderAvatarUrl?: string | null;
  content?: string | null;
  createdAt?: number | null;
  /** Likes (2026-09-03, additiv): fehlt bei alten Antworten → 0 / false */
  likeCount?: number;
  likedByMe?: boolean;
  /** Emoji-Reaktionen (Marcel 2026-10-24, additiv): fehlt bei alten Antworten → [] */
  reactionSummary?: ReactionSummary[];
  [key: string]: any;
}

export interface LiveChatLikeResult {
  id: string;
  liked: boolean;
  likeCount: number;
}

/** Antwort von POST /api/live-chat/message/:id/reaction */
export interface LiveChatReactionResult {
  id: string;
  action: 'added' | 'changed' | 'removed';
  emoji: string;
  reactionSummary: ReactionSummary[];
}

/** Socket-Payload `liveChatMessageReacted` (reactionSummary ohne Viewer-Bezug: mine=false, userIds gefüllt) */
export interface LiveChatReactionEvent {
  id?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  userId?: string | null;
  emoji?: string | null;
  action?: string | null;
  reactionSummary?: ReactionSummary[] | null;
}

/** PURE + defensiv: Backend-Summary → ReactionSummary[]; „mine“ aus userIds + viewerId (Socket-Events tragen mine=false) */
export function parseReactionSummary(raw: unknown, viewerId: string | null = null): ReactionSummary[] {
  if (!Array.isArray(raw)) return [];
  const out: ReactionSummary[] = [];
  for (const r of raw) {
    const emoji = typeof r?.emoji === 'string' ? r.emoji : '';
    if (!emoji) continue;
    const userIds = Array.isArray(r.userIds) ? r.userIds.map((u: unknown) => String(u)) : [];
    const count = typeof r.count === 'number' && r.count >= 0 ? Math.floor(r.count) : userIds.length;
    const mine = viewerId != null ? userIds.includes(String(viewerId)) || r.mine === true : r.mine === true;
    out.push({ emoji, count, mine, userIds });
  }
  return out;
}

/** Socket-Payload `liveChatMessageLiked` */
export interface LiveChatLikeEvent {
  id?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  likeCount?: number;
  userId?: string | null;
  liked?: boolean;
}

export interface LiveChatMessagesResult {
  messages: LiveChatMessage[];
  hasMore: boolean;
}

/**
 * PURE: Reaktion des Viewers lokal auf eine Summary anwenden (optimistisch, gleiche Regel wie Backend):
 * gleiche Reaktion → weg, andere → ersetzt, keine → hinzugefügt. Reihenfolge nach Zähler, leere Einträge raus.
 */
export function toggleReactionInSummary(summary: ReactionSummary[] | null | undefined, viewerId: string, emoji: string): ReactionSummary[] {
  const me = String(viewerId);
  const list = (Array.isArray(summary) ? summary : []).map((s) => ({ ...s, userIds: [...(s.userIds ?? [])] }));
  const mineEntry = list.find((s) => s.userIds.includes(me) || s.mine);
  const sameEmoji = mineEntry?.emoji === emoji;
  for (const s of list) {
    if (s.userIds.includes(me) || s.mine) {
      s.userIds = s.userIds.filter((u) => u !== me);
      s.count = Math.max(0, s.count - 1);
      s.mine = false;
    }
  }
  if (!sameEmoji) {
    const target = list.find((s) => s.emoji === emoji);
    if (target) { target.userIds.push(me); target.count += 1; target.mine = true; }
    else list.push({ emoji, count: 1, mine: true, userIds: [me] });
  }
  return list.filter((s) => s.count > 0).sort((a, b) => b.count - a.count);
}

function parseMessage(data: any): LiveChatMessage | null {
  if (!data || typeof data !== 'object' || !data.id) return null;
  return {
    id: data.id ?? null,
    entityType: data.entityType ?? null,
    entityId: data.entityId ?? null,
    senderId: data.senderId ?? null,
    senderName: data.senderName ?? null,
    senderAvatarUrl: data.senderAvatarUrl ?? null,
    content: data.content ?? null,
    createdAt: typeof data.createdAt === 'number' ? data.createdAt : null,
    likeCount: typeof data.likeCount === 'number' && data.likeCount >= 0 ? data.likeCount : 0,
    likedByMe: data.likedByMe === true,
    reactionSummary: parseReactionSummary(data.reactionSummary),
  };
}

const liveChatService = {
  /** Socket.io-Channel eines Chat-Raums (muss exakt zum Backend passen) */
  buildChannelName(segment: LiveChatEntitySegment, entityId: string): string {
    return `live-chat:${SEGMENT_TO_ENUM[segment]}:${entityId}`;
  },

  /** Nachrichten laden (chronologisch aufsteigend); `before` lädt ältere nach */
  async getMessages(
    segment: LiveChatEntitySegment,
    entityId: string,
    options: { limit?: number; before?: number | null } = {},
  ): Promise<LiveChatMessagesResult> {
    const params: Record<string, any> = {};
    if (options.limit) params.limit = options.limit;
    if (options.before != null) params.before = options.before;
    const res = await api.get(`/api/live-chat/${segment}/${entityId}/messages`, { params });
    const raw = Array.isArray(res.data?.messages) ? res.data.messages : [];
    return {
      messages: raw.map(parseMessage).filter(Boolean) as LiveChatMessage[],
      hasMore: res.data?.hasMore === true,
    };
  },

  /** Nachricht senden (nur angemeldet) */
  async postMessage(
    segment: LiveChatEntitySegment,
    entityId: string,
    content: string,
  ): Promise<LiveChatMessage | null> {
    const res = await api.post(`/api/live-chat/${segment}/${entityId}/messages`, { content });
    return parseMessage(res.data);
  },

  /** Nachricht löschen (Absender oder DC-Admin) */
  async deleteMessage(messageId: string): Promise<void> {
    await api.post(`/api/live-chat/message/${messageId}/delete`);
  },

  /** Like einer Nachricht togglen (nur angemeldet) → { id, liked, likeCount } */
  async toggleLike(messageId: string): Promise<LiveChatLikeResult> {
    const res = await api.post(`/api/live-chat/message/${encodeURIComponent(messageId)}/like`);
    const data = res?.data ?? {};
    return {
      id: typeof data.id === 'string' ? data.id : messageId,
      liked: data.liked === true,
      likeCount: typeof data.likeCount === 'number' && data.likeCount >= 0 ? data.likeCount : 0,
    };
  },

  /** Emoji-Reaktion umschalten (Marcel 2026-10-24, nur angemeldet): gleiche → weg, andere → ersetzt */
  async toggleReaction(messageId: string, emoji: string): Promise<LiveChatReactionResult> {
    const res = await api.post(`/api/live-chat/message/${encodeURIComponent(messageId)}/reaction`, { emoji });
    const data = res?.data ?? {};
    const action = data.action === 'removed' || data.action === 'changed' ? data.action : 'added';
    return {
      id: typeof data.id === 'string' ? data.id : messageId,
      action,
      emoji: typeof data.emoji === 'string' ? data.emoji : emoji,
      reactionSummary: parseReactionSummary(data.reactionSummary),
    };
  },
};

export default liveChatService;
